/**
 * Comprehensive Date Utilities
 * Handles all date calculations, formatting, and validation for ParkProUI
 */

import { parse, format, addDays, subDays, differenceInDays, isValid, parseISO, startOfDay } from 'date-fns';

class DateUtils {
  constructor() {
    // Contextually appropriate date formats
    this.formats = {
      // Form inputs and data storage
      input: 'yyyy-MM-dd', // "2024-01-15" - HTML input format
      
      // List views and tables (compact)
      list: 'MMM d, yyyy', // "Jan 15, 2024" - Compact but readable
      table: 'MM/dd/yyyy', // "01/15/2024" - Very compact for tables
      
      // Card displays and detailed views
      card: 'EEEE, MMMM d, yyyy', // "Monday, January 15, 2024" - Full and friendly
      detail: 'MMMM d, yyyy', // "January 15, 2024" - Clear and readable
      
      // Trip planning and scheduling
      trip: 'MMM d', // "Jan 15" - For day-by-day trip views
      schedule: 'EEE, MMM d', // "Mon, Jan 15" - For scheduling contexts
      
      // Time displays
      time: 'h:mm a', // "2:30 PM"
      datetime: 'MMM d, yyyy h:mm a', // "Jan 15, 2024 2:30 PM"
      
      // Special contexts
      relative: 'MMM d, yyyy', // "Jan 15, 2024" - For relative date fallbacks
      range: 'MMM d', // "Jan 15" - For date ranges
      
      // Legacy support
      display: 'MMM d, yyyy', // "Jan 15, 2024" - Default readable format
      short: 'MM/dd/yyyy', // "01/15/2024" - Compact format
      long: 'EEEE, MMMM d, yyyy' // "Monday, January 15, 2024" - Full format
    };
  }

  /**
   * Parse a date string into a Date object
   * Handles multiple input formats
   */
  parseDate(dateString) {
    if (!dateString) return null;
    
    try {
      // Handle different input formats
      if (typeof dateString === 'string') {
        // Handle MM/dd/yyyy format
        if (dateString.includes('/')) {
          const [month, day, year] = dateString.split('/');
          const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
          return isValid(date) ? date : null;
        }
        
        // Handle ISO format or other standard formats
        const parsed = parseISO(dateString);
        if (isValid(parsed)) return parsed;
        
        // Try parsing as regular date
        const regularDate = new Date(dateString);
        return isValid(regularDate) ? regularDate : null;
      }
      
      // Handle Date objects
      if (dateString instanceof Date) {
        return isValid(dateString) ? dateString : null;
      }
      
      return null;
    } catch (error) {
      console.error('Error parsing date:', dateString, error);
      return null;
    }
  }

  /**
   * Format a date for display
   */
  formatDate(dateString, formatType = 'display') {
    const date = this.parseDate(dateString);
    if (!date) return 'Date TBD';
    
    try {
      return format(date, this.formats[formatType] || formatType);
    } catch (error) {
      console.error('Error formatting date:', dateString, error);
      return 'Invalid Date';
    }
  }

  /**
   * Format a date for input fields (YYYY-MM-DD)
   */
  formatForInput(dateString) {
    const date = this.parseDate(dateString);
    if (!date) return '';
    
    try {
      return format(date, this.formats.input);
    } catch (error) {
      console.error('Error formatting date for input:', dateString, error);
      return '';
    }
  }

  /**
   * Calculate trip duration in days
   */
  calculateTripDays(startDate, endDate) {
    const start = this.parseDate(startDate);
    const end = this.parseDate(endDate);
    
    if (!start || !end) return 0;
    
    try {
      const days = differenceInDays(end, start) + 1; // +1 to include both start and end days
      return Math.max(0, days);
    } catch (error) {
      console.error('Error calculating trip days:', startDate, endDate, error);
      return 0;
    }
  }

  /**
   * Get trip length in days (inclusive). Returns null until both dates are valid.
   * Used for trip-length-based UI (e.g. 1–2 day vs 3+ day rules).
   */
  getTripLengthDays(startDate, endDate) {
    const start = this.parseDate(startDate);
    const end = this.parseDate(endDate);

    if (!start || !end) return null;

    try {
      const days = differenceInDays(end, start) + 1;
      return days >= 1 ? days : null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Generate date sequence for trip days
   */
  generateTripDates(startDate, endDate) {
    const start = this.parseDate(startDate);
    const end = this.parseDate(endDate);
    
    if (!start || !end) return [];
    
    try {
      const dates = [];
      const totalDays = this.calculateTripDays(startDate, endDate);
      
      for (let i = 0; i < totalDays; i++) {
        const currentDate = addDays(start, i);
        dates.push({
          date: this.formatForInput(currentDate),
          displayDate: this.formatDate(currentDate),
          dayNumber: i + 1,
          dayOfWeek: format(currentDate, 'EEEE'),
          shortDayOfWeek: format(currentDate, 'EEE')
        });
      }
      
      return dates;
    } catch (error) {
      console.error('Error generating trip dates:', startDate, endDate, error);
      return [];
    }
  }

  /**
   * Check if a date is valid
   */
  isValidDate(dateString) {
    const date = this.parseDate(dateString);
    return date !== null;
  }

  /**
   * Check if a date is in the future
   */
  isFutureDate(dateString) {
    const date = this.parseDate(dateString);
    if (!date) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    return date > today;
  }

  /**
   * Check if a date is in the past
   */
  isPastDate(dateString) {
    const date = this.parseDate(dateString);
    if (!date) return false;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Reset time to start of day
    
    return date < today;
  }

  /**
   * Get today's date in input format
   */
  getTodayInput() {
    return this.formatForInput(new Date());
  }

  /**
   * Get a date X days from today
   */
  getDateFromToday(days) {
    const date = addDays(new Date(), days);
    return this.formatForInput(date);
  }

  /**
   * Format trip date range with contextually appropriate formatting
   */
  formatTripDateRange(startDate, endDate, context = 'default') {
    const start = this.parseDate(startDate);
    const end = this.parseDate(endDate);
    
    if (!start || !end) return 'Not specified';
    
    try {
      if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
        // Same month and year
        if (context === 'table' || context === 'compact') {
          // Compact format: "Jan 15-18, 2024"
          const startDay = format(start, 'MMM d');
          const endDay = format(end, 'd, yyyy');
          return `${startDay}-${endDay}`;
        } else {
          // Readable format: "Jan 15 - 18, 2024"
          const startDay = format(start, 'MMM d');
          const endDay = format(end, 'd, yyyy');
          return `${startDay} - ${endDay}`;
        }
      } else if (start.getFullYear() === end.getFullYear()) {
        // Same year
        if (context === 'table' || context === 'compact') {
          // Compact format: "Jan 15 - Mar 18, 2024"
          const startMonthDay = format(start, 'MMM d');
          const endMonthDay = format(end, 'MMM d, yyyy');
          return `${startMonthDay} - ${endMonthDay}`;
        } else {
          // Readable format: "Jan 15 - Mar 18, 2024"
          const startMonthDay = format(start, 'MMM d');
          const endMonthDay = format(end, 'MMM d, yyyy');
          return `${startMonthDay} - ${endMonthDay}`;
        }
      } else {
        // Different years
        if (context === 'table' || context === 'compact') {
          // Compact format: "Jan 15, 2024 - Jan 18, 2025"
          const startFormatted = format(start, 'MMM d, yyyy');
          const endFormatted = format(end, 'MMM d, yyyy');
          return `${startFormatted} - ${endFormatted}`;
        } else {
          // Readable format: "Jan 15, 2024 - Jan 18, 2025"
          const startFormatted = format(start, 'MMM d, yyyy');
          const endFormatted = format(end, 'MMM d, yyyy');
          return `${startFormatted} - ${endFormatted}`;
        }
      }
    } catch (error) {
      console.error('Error formatting trip date range:', startDate, endDate, error);
      return 'Invalid date range';
    }
  }

  /**
   * Format generated date (for itinerary creation) with context
   */
  formatGeneratedDate(dateString, context = 'default') {
    const date = this.parseDate(dateString);
    if (!date) return 'Recently created';
    
    try {
      const now = new Date();
      const diffInHours = Math.abs(now - date) / (1000 * 60 * 60);
      
      if (diffInHours < 1) {
        return 'Just now';
      } else if (diffInHours < 24) {
        const hours = Math.floor(diffInHours);
        return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      } else if (diffInHours < 48) {
        return 'Yesterday';
      } else {
        // Use contextually appropriate format for older dates
        if (context === 'table' || context === 'compact') {
          return this.formatDate(date, 'table'); // "01/15/2024" - Very compact
        } else if (context === 'list') {
          return this.formatDate(date, 'list'); // "Jan 15, 2024" - Compact but readable
        } else {
          return this.formatDate(date, 'relative'); // "Jan 15, 2024" - Default readable
        }
      }
    } catch (error) {
      console.error('Error formatting generated date:', dateString, error);
      return 'Recently created';
    }
  }

  /**
   * Validate date range (start before end)
   */
  validateDateRange(startDate, endDate) {
    const start = this.parseDate(startDate);
    const end = this.parseDate(endDate);
    
    if (!start || !end) {
      return { isValid: false, error: 'Invalid dates' };
    }
    
    if (start > end) {
      return { isValid: false, error: 'Start date must be before end date' };
    }
    
    const days = this.calculateTripDays(startDate, endDate);
    if (days > 365) {
      return { isValid: false, error: 'Trip cannot exceed 365 days' };
    }
    
    return { isValid: true };
  }

  /**
   * Get relative date description with context
   */
  getRelativeDate(dateString, context = 'default') {
    const date = this.parseDate(dateString);
    if (!date) return 'Unknown date';
    
    try {
      const now = new Date();
      // Normalize both dates to start of day for accurate calendar day comparison
      const dateStart = startOfDay(date);
      const nowStart = startOfDay(now);
      const diffInDays = differenceInDays(dateStart, nowStart);
      
      if (diffInDays === 0) return 'Today';
      if (diffInDays === 1) return 'Tomorrow';
      if (diffInDays === -1) return 'Yesterday';
      if (diffInDays > 0) return `In ${diffInDays} days`;
      if (diffInDays < 0) return `${Math.abs(diffInDays)} days ago`;
      
      // Use contextually appropriate format for distant dates
      if (context === 'table' || context === 'compact') {
        return this.formatDate(date, 'table'); // "01/15/2024" - Very compact
      } else if (context === 'list') {
        return this.formatDate(date, 'list'); // "Jan 15, 2024" - Compact but readable
      } else if (context === 'trip' || context === 'schedule') {
        return this.formatDate(date, 'trip'); // "Jan 15" - For trip planning
      } else {
        return this.formatDate(date, 'relative'); // "Jan 15, 2024" - Default readable
      }
    } catch (error) {
      console.error('Error getting relative date:', dateString, error);
      return this.formatDate(date, 'relative');
    }
  }

  /**
   * Parse and normalize itinerary day dates
   */
  normalizeItineraryDates(dailyItineraries, startDate) {
    if (!Array.isArray(dailyItineraries) || !startDate) {
      return [];
    }
    
    const start = this.parseDate(startDate);
    if (!start) return dailyItineraries;
    
    try {
      return dailyItineraries.map((day, index) => {
        const dayDate = addDays(start, index);
        return {
          ...day,
          date: this.formatForInput(dayDate),
          displayDate: this.formatDate(dayDate),
          dayNumber: index + 1,
          dayOfWeek: format(dayDate, 'EEEE'),
          shortDayOfWeek: format(dayDate, 'EEE')
        };
      });
    } catch (error) {
      console.error('Error normalizing itinerary dates:', error);
      return dailyItineraries;
    }
  }

  /**
   * Get timezone-aware date formatting with context
   */
  formatDateWithTimezone(dateString, timezone = 'America/New_York', context = 'default') {
    const date = this.parseDate(dateString);
    if (!date) return 'Date TBD';
    
    try {
      if (context === 'table' || context === 'compact') {
        // Compact format: "01/15/2024"
        return new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          year: 'numeric',
          month: '2-digit',
          day: '2-digit'
        }).format(date);
      } else if (context === 'list') {
        // List format: "Jan 15, 2024"
        return new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }).format(date);
      } else if (context === 'card' || context === 'detail') {
        // Full format: "Monday, January 15, 2024"
        return new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric'
        }).format(date);
      } else {
        // Default format: "Jan 15, 2024"
        return new Intl.DateTimeFormat('en-US', {
          timeZone: timezone,
          year: 'numeric',
          month: 'short',
          day: 'numeric'
        }).format(date);
      }
    } catch (error) {
      console.error('Error formatting date with timezone:', dateString, error);
      return this.formatDate(dateString);
    }
  }

  /**
   * Context-aware helper methods for common UI patterns
   */
  
  /**
   * Format date for table display (very compact)
   */
  formatForTable(dateString) {
    return this.formatDate(dateString, 'table');
  }

  /**
   * Format date for list display (compact but readable)
   */
  formatForList(dateString) {
    return this.formatDate(dateString, 'list');
  }

  /**
   * Format date for card display (full and friendly)
   */
  formatForCard(dateString) {
    return this.formatDate(dateString, 'card');
  }

  /**
   * Format date for detail view (clear and readable)
   */
  formatForDetail(dateString) {
    return this.formatDate(dateString, 'detail');
  }

  /**
   * Format date for trip planning (day-focused)
   */
  formatForTrip(dateString) {
    return this.formatDate(dateString, 'trip');
  }

  /**
   * Format date for scheduling (weekday + date)
   */
  formatForSchedule(dateString) {
    return this.formatDate(dateString, 'schedule');
  }

  /**
   * Format trip date range for table (compact)
   */
  formatTripRangeForTable(startDate, endDate) {
    return this.formatTripDateRange(startDate, endDate, 'table');
  }

  /**
   * Format trip date range for list (readable)
   */
  formatTripRangeForList(startDate, endDate) {
    return this.formatTripDateRange(startDate, endDate, 'default');
  }

  /**
   * Format generated date for table (compact)
   */
  formatGeneratedForTable(dateString) {
    return this.formatGeneratedDate(dateString, 'table');
  }

  /**
   * Format generated date for list (readable)
   */
  formatGeneratedForList(dateString) {
    return this.formatGeneratedDate(dateString, 'list');
  }
}

// ---------------------------------------------------------------------------
// Canonical date parsing for comparisons (validation, filtering, trip status)
// Use these for any date comparison; use formatDate/display helpers for UI only.
// ---------------------------------------------------------------------------

/**
 * Returns Date at local midnight for "YYYY-MM-DD" or null if invalid.
 */
export function parseYMDLocal(ymdString) {
  if (!ymdString || typeof ymdString !== 'string') return null;
  const match = ymdString.trim().match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const [, y, m, d] = match;
  const year = parseInt(y, 10);
  const month = parseInt(m, 10) - 1;
  const day = parseInt(d, 10);
  if (month < 0 || month > 11) return null;
  const date = new Date(year, month, day);
  if (date.getFullYear() !== year || date.getMonth() !== month || date.getDate() !== day) return null;
  return date;
}

/**
 * Sets hours to 0,0,0,0 and returns new Date clone (start of local day).
 */
export function startOfLocalDay(date) {
  if (!date || !(date instanceof Date)) return null;
  const d = new Date(date.getTime());
  d.setHours(0, 0, 0, 0);
  return d;
}

/**
 * Parse a value for date comparison. Returns Date at start of local day or null.
 * - Date input => startOfLocalDay(Date)
 * - "YYYY-MM-DD" => parseYMDLocal
 * - other string/number => new Date(value) then startOfLocalDay
 * - invalid => null
 */
export function parseDateForCompare(value) {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null;
    return startOfLocalDay(value);
  }
  if (typeof value === 'string' && value.trim().match(/^\d{4}-\d{2}-\d{2}$/)) {
    const parsed = parseYMDLocal(value);
    return parsed ? startOfLocalDay(parsed) : null;
  }
  const d = new Date(value);
  if (isNaN(d.getTime())) return null;
  return startOfLocalDay(d);
}

/**
 * Fill missing/invalid day.date from trip start date. Only sets date for days
 * with no date or invalid date; does not overwrite existing valid day.date.
 */
export function fillMissingDayDates(days, startDateStr) {
  if (!Array.isArray(days) || days.length === 0) return days;
  const start = parseYMDLocal(startDateStr) || (startDateStr ? parseDateForCompare(startDateStr) : null);
  if (!start) return days;
  const hasAnyMissing = days.some((day, i) => {
    const v = day?.date;
    return v == null || v === '' || parseDateForCompare(v) === null;
  });
  if (!hasAnyMissing) return days;
  return days.map((day, index) => {
    const v = day?.date;
    if (v != null && v !== '' && parseDateForCompare(v) !== null) return day;
    const dayDate = addDays(start, index);
    return { ...day, date: format(dayDate, 'yyyy-MM-dd') };
  });
}

// Export singleton instance
export default new DateUtils();

// ---------------------------------------------------------------------------
// V2 time formatting: minutes from midnight -> "9:30 AM"
// ---------------------------------------------------------------------------

/**
 * Converts minutes from midnight to a 12-hour time string.
 * @param {number} minutes - Minutes from midnight (e.g. 570 = 9:30 AM)
 * @returns {string} Formatted time, e.g. "9:30 AM"
 */
export function minutesToTimeStr(minutes) {
  if (minutes == null || typeof minutes !== 'number' || Number.isNaN(minutes)) return '';
  const h = Math.floor(minutes / 60) % 24;
  const m = Math.floor(minutes % 60);
  const hour = h === 0 ? 12 : h > 12 ? h - 12 : h;
  const ampm = h < 12 ? 'AM' : 'PM';
  return `${hour}:${String(m).padStart(2, '0')} ${ampm}`;
}

/**
 * Format a time range from startMin to endMin.
 * @param {number} startMin - Start minutes from midnight
 * @param {number|null} endMin - End minutes (optional)
 * @returns {string} e.g. "9:30 AM – 10:15 AM" or "9:30 AM"
 */
export function formatTimeRange(startMin, endMin) {
  const start = minutesToTimeStr(startMin);
  if (start === '') return '';
  if (endMin == null || typeof endMin !== 'number') return start;
  const end = minutesToTimeStr(endMin);
  if (end === '' || start === end) return start;
  return `${start} – ${end}`;
}
